import type { Message, MutationProposal } from "~/helpers/aiChat/chatStorage";
import type { NavConfirmation } from "~/helpers/aiChat/fernieActions";
import type { AssistantTurn } from "~/services/aiOrchestrator";
import type { FernieAction } from "~/store/fernieActionSlice";

import buildToolResults from "~/helpers/aiChat/buildToolResults";
import extractArtifacts from "~/helpers/aiChat/extractArtifacts";
import processFernieActions from "~/helpers/aiChat/fernieActions";
import buildActionFeedback, { composeFeedback, isOverClaim, navigationFailed } from "~/helpers/aiChat/fernieFeedback";
import { buildIntakeDrafts } from "~/helpers/aiChat/fernieIntake";

const INTAKE_SUBMISSION_TOOL = "wsapi-submit-feedback-request";

export interface BuiltAssistantMessage {
	dispatchedActions: FernieAction[];
	feedbackTargetId: string | null;
	message: Message;
	ranNavigation: boolean;
}

const buildAssistantMessage = (
	turn: AssistantTurn,
	messageId: string,
	canOpen: (path: string) => boolean
): BuiltAssistantMessage => {
	const { artifacts, text: cleanText } = extractArtifacts(turn.assistantMessage);
	const fernieActions = processFernieActions(turn.actions, canOpen);
	const navConfirmation: NavConfirmation | undefined = fernieActions.confirmations[0];
	const ranNavigation =
		fernieActions.dispatched.length > 0 || fernieActions.skipped.length > 0 || fernieActions.confirmations.length > 0;
	const toolResults = ranNavigation ? [] : buildToolResults(turn.toolCalls);
	const derivedFeedback = buildActionFeedback(fernieActions);
	const suppressNavProse =
		navigationFailed(fernieActions) ||
		fernieActions.confirmations.length > 0 ||
		(ranNavigation && isOverClaim(cleanText));
	const guarded = suppressNavProse ? "" : cleanText;
	const content = derivedFeedback ? composeFeedback(guarded, derivedFeedback) : guarded;
	const proposals: MutationProposal[] = turn.proposals
		.filter((proposal) => proposal.toolName !== INTAKE_SUBMISSION_TOOL)
		.map((proposal) => ({
			argumentsJson: proposal.argumentsJson,
			id: proposal.id,
			priceImport: proposal.priceImport ?? undefined,
			state: proposal.state,
			summary: proposal.summary,
			toolName: proposal.toolName
		}));
	const intakeDrafts = buildIntakeDrafts(turn.intakeCaptures ?? []);
	const message: Message = {
		content,
		htmlArtifacts: artifacts.length > 0 ? artifacts : undefined,
		id: messageId,
		intakeDrafts: intakeDrafts.length > 0 ? intakeDrafts : undefined,
		layouts: turn.layouts && turn.layouts.length > 0 ? turn.layouts : undefined,
		navConfirmation,
		proposals: proposals.length > 0 ? proposals : undefined,
		reportArtifacts: turn.artifacts && turn.artifacts.length > 0 ? turn.artifacts : undefined,
		role: "assistant",
		toolResults: toolResults.length > 0 ? toolResults : undefined
	};
	return {
		dispatchedActions: fernieActions.dispatched,
		feedbackTargetId: fernieActions.dispatched.length > 0 ? messageId : null,
		message,
		ranNavigation
	};
};

export default buildAssistantMessage;

export { buildAssistantMessage };
